import { useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Redirect, useLocation } from 'react-router-dom';
import {
  Alert,
  Button,
  Dialog,
  Icon,
  Typography,
} from '@vivaahealth/design-system';

import styles from './add-insurance-page.module.css';

import * as api from '../../../../../api';

interface AddInsurancePageProps extends WithTranslation {
  isRequired: boolean;
  cards: api.PatientInsuranceInformation.InsuranceInformationWithDetails[];
  navigateToNextPage: () => Promise<void>;
  handleInsuranceChoice: (
    payingWithInsurance: boolean,
    skippingInsurance: boolean,
  ) => Promise<void>;
  onAddNewCard: () => Promise<void>;
}

const AddInsurancePage = ({
  isRequired,
  cards,
  navigateToNextPage,
  handleInsuranceChoice,
  t,
  onAddNewCard,
}: AddInsurancePageProps) => {
  const location = useLocation();

  const [confirmSkipOpen, setConfirmSkipOpen] = useState(false);
  const [skippingInsurance, setSkippingInsurance] = useState(false);
  const [payingWithoutInsurance, setPayingWithoutInsurance] = useState(false);

  if (cards.length !== 0) {
    return <Redirect to={location.pathname + '/review' + location.search} />;
  }

  const skipInsurance = async () => {
    setConfirmSkipOpen(false);
    await handleInsuranceChoice(!payingWithoutInsurance, skippingInsurance);
    await navigateToNextPage();
  };

  return (
    <div className={styles.container}>
      <Typography variant="headline-small" bold>
        {t('title')}
      </Typography>
      <Alert variant="info" className={styles.alert}>
        {t('alert')}
      </Alert>
      <Button fullWidth data-testid="add-insurance" onClick={onAddNewCard}>
        {t('addNewCard', { ns: 'uploadCard' })}
      </Button>
      {!isRequired && (
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            setPayingWithoutInsurance(false);
            setSkippingInsurance(true);
            setConfirmSkipOpen(true);
          }}
        >
          {t('addAtAppointment')}
        </Button>
      )}

      <Button
        fullWidth
        variant="plain"
        onClick={() => {
          setSkippingInsurance(false);
          setPayingWithoutInsurance(true);
          setConfirmSkipOpen(true);
        }}
      >
        {t('doNotUseInsurance', { ns: 'insurance' })}
      </Button>

      <Dialog
        Icon={<Icon.AlertTriangle color={'#FFAE00'} size={'large'} />}
        open={confirmSkipOpen}
        description={t(
          payingWithoutInsurance
            ? 'confirmOutOfPocket'
            : 'confirmReviewAtAppointment',
        )}
        title={
          payingWithoutInsurance
            ? t('doNotUseInsuranceQuestion', { ns: 'insurance' })
            : t('provideCardAtAppointmentQuestion')
        }
      >
        <Button fullWidth size="medium" onClick={skipInsurance}>
          {payingWithoutInsurance
            ? t('doNotUseInsurance', { ns: 'insurance' })
            : t('addAtAppointment')}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          size="medium"
          onClick={() => setConfirmSkipOpen(false)}
        >
          {t('goBack', { ns: 'insurance' })}
        </Button>
      </Dialog>
    </div>
  );
};

export default withTranslation(['addInsurancePage', 'insurance', 'uploadCard'])(
  AddInsurancePage,
);
